import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { isNil, orderBy } from 'lodash';
import { Tooltip, Popper, ClickAwayListener, makeStyles, createStyles, Zoom } from '@material-ui/core';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { AppState } from 'src/store';
import { AdornmentType, AdornmentsEnum } from 'configuration-schemas/viewdefns/literals';
import { WorklistIcon, WorklistType, worklistStatusMap } from 'src/components/WorklistIcon/WorklistIcon';
import { BasicPivotItem, WorklistInfo } from 'src/worker/pivotWorker.types';
import { getWorklistMenuItems, ContextMenuType } from 'src/components/WorklistContextMenu/WorklistContextMenu';
import { WorklistContextMenu } from 'src/components/WorklistContextMenu/WorklistContextMenuReact';
import { ID } from 'src/utils/Domain/Constants';
import { TEAL_PRIMARY } from 'src/utils/Style/Theme';
import { PlanItem, PlanItemStatus } from 'src/pages/AssortmentBuild/Planning.types';
import { getActivePerspectiveWorklistConfigButtonsConfig } from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';
import { WorklistConfigButtons } from 'configuration-schemas/confdefnView';
import { PlanningStatusIcon } from './PlanningStatusAdornmentRenderer';

const useStyles = makeStyles((_theme) =>
  createStyles({
    container: {
      height: 24,
      display: 'flex',
      justifyContent: 'space-between',
      maxWidth: 'fit-content',
      textAlign: 'center',
    },
    exceptionRoot: {
      fontSize: '1.35rem',
      marginRight: '0.1rem',
    },
    worklistRoot: {
      fontSize: '1.2rem',
      paddingRight: '0.15rem',
    },
    popper: {
      border: '1px solid rgba(0,0,0,.2)',
      borderRadius: '10px',
      maxWidth: '1000px',
      backgroundColor: 'white',
    },
    planStatusIcon: {
      color: TEAL_PRIMARY,
      fontSize: '1.1rem',
      lineHeight: 1,
    },
  })
);

export interface AdornmentContextOptions {
  type: ContextMenuType;
  parentIdentityField: 'style' | 'styleId';
  nodeData: unknown;
  onOpenStylePane: () => void;
}

export interface AdornmentsOwnProps {
  productId: string;
  adornments: AdornmentType[];
  contextOptions?: AdornmentContextOptions;
  /**
   * removes the pointer style from the worklist adornment
   * since grids interact with worklist items differently */
  interactive?: boolean;
}

interface AdornmentsProps extends AdornmentsOwnProps {
  worklist: WorklistInfo[];
  planItems: PlanItem[];
  exceptions: never[];
  worklistConfigButtons: WorklistConfigButtons | undefined;
}

function mapStateToProps(state: AppState, ownProps: AdornmentsOwnProps): AdornmentsProps {
  const worklist = state.pages.hindsighting.styleColorReview.sharedData.worklist;
  const worklistConfigButtons = getActivePerspectiveWorklistConfigButtonsConfig(state);
  return {
    ...ownProps,
    worklist,
    planItems: state.planTracker.planItems || ([] as PlanItem[]),
    exceptions: [],
    worklistConfigButtons,
  };
}

// in case multiple adornments are present, worklist first since it is interactive
const ADORNMENT_WEIGHTS = {
  [AdornmentsEnum.worklist]: 0,
  [AdornmentsEnum.exception]: 1,
  [AdornmentsEnum.planning]: 2,
};

const Adornments = (props: AdornmentsProps) => {
  const { adornments, productId, contextOptions, interactive, worklist, planItems, exceptions, worklistConfigButtons } =
    props;
  const styles = useStyles();
  const anchorRef = useRef(null);
  const [isPopperOpen, setPopperOpen] = useState(false);
  const openPopper = () => setPopperOpen(true);
  const closePopper = () => setPopperOpen(false);
  const orderedAdornments = orderBy(adornments, (a) => ADORNMENT_WEIGHTS[a]);
  const worklistContextMenuItems = worklistConfigButtons
    ? getWorklistMenuItems('singleSelect', {
      identityField: ID,
      parentIdentityField: contextOptions?.parentIdentityField,
      type: contextOptions?.type,
      openStylePane: contextOptions?.onOpenStylePane,
      onPostAction: closePopper,
      params: {
        // @ts-ignore - check this? wouldn't allow this assignment because it was missing all the IRowNodeProperties
        node: { data: contextOptions?.nodeData },
      },
      worklistConfigButtons,
    })
    : [];

  return (
    <React.Fragment>
      <div data-qa="adornments-container" className={styles.container}>
        {orderedAdornments.map((adornment) => {
          switch (adornment) {
            case AdornmentsEnum.worklist: {
              const item = worklist.find((item) => item.product === productId);
              if (isNil(item)) {
                return null;
              }
              const type = item.type;
              const status = `Worklist status - ${worklistStatusMap[type]}`;
              return (
                <Zoom
                  key={`worklist-grow-${productId}-${type}`} // rerun if worklist type changes
                  in={true}
                  appear
                  timeout={220}
                >
                  <Tooltip title={status}>
                    <span
                      className={`${styles.worklistRoot} worklistAdornment`}
                      style={{
                        marginLeft: contextOptions?.type === ContextMenuType.stylePreview ? '0.3rem' : 0,
                      }}
                      ref={anchorRef}
                      onClick={openPopper}
                    >
                      <WorklistIcon icon={type as WorklistType} interactive={interactive} />
                    </span>
                  </Tooltip>
                </Zoom>
              );
            }
            // FIXME: This is used for janky planogram. Super hardcode ACTIVATE
            case AdornmentsEnum.planogram: {
              const item = contextOptions?.nodeData as BasicPivotItem;
              if (isNil(item)) {
                return null;
              }
              const type = item.worklist_type;
              // @ts-ignore
              let rec = '';
              switch (type) {
                case 'added':
                  rec = 'Recommend add.';
                  break;
                case 'dropped':
                  rec = 'Recommend drop.';
              }
              const status = rec;
              return (
                <Tooltip key={`worklist-${productId}`} title={status}>
                  <span
                    className={`${styles.worklistRoot} worklistAdornment`}
                    style={{
                      marginLeft: contextOptions && contextOptions.type === ContextMenuType.stylePreview ? '0.3rem' : 0,
                    }}
                    ref={anchorRef}
                    onClick={openPopper}
                  >
                    <WorklistIcon icon={type as WorklistType} interactive={interactive} />
                  </span>
                </Tooltip>
              );
            }
            case AdornmentsEnum.exception: {
              const item = exceptions.find((item: unknown) => item === productId);
              if (isNil(item)) {
                return null;
              }
              return (
                <Zoom key={`exception-grow-${productId}`} in={true} appear timeout={180}>
                  <Tooltip title="This item has exceptions">
                    <WarningRoundedIcon color="error" className={styles.exceptionRoot} />
                  </Tooltip>
                </Zoom>
              );
            }
            case AdornmentsEnum.planning: {
              const item = planItems.find((item) => item.id === productId || item.hierarchy.includes(productId));
              if (isNil(item)) {
                return null;
              }
              // leaving out 'queued' & 'predicting' for icons/tooltips
              const tooltipByStatus: Partial<Record<PlanItemStatus, string>> = {
                [PlanItemStatus.Pending]: 'This item has pending unplanned changes',
                [PlanItemStatus.Processing]: 'Planning changes for this item are processing',
                [PlanItemStatus.Completed]: 'Planning changes for this item completed',
                [PlanItemStatus.Failed]: 'Planning changes for this item failed',
              };

              const tooltip =
                tooltipByStatus[item.status] ?? `Planning status: ${item.status}`;


              return (
                <Tooltip title={tooltip}>
                  <span>
                    <PlanningStatusIcon status={item.status} />
                  </span>
                </Tooltip>
              );
            }

            default:
              return <div>Unknown adornment configured</div>;
          }
        })}
      </div>
      {contextOptions && (
        <Popper open={isPopperOpen} anchorEl={anchorRef.current} placement="bottom-start" className={styles.popper}>
          <ClickAwayListener onClickAway={closePopper}>
            <div>
              <WorklistContextMenu worklistContextMenuItems={worklistContextMenuItems} />
            </div>
          </ClickAwayListener>
        </Popper>
      )}
    </React.Fragment>
  );
};

export default connect(mapStateToProps)(Adornments);
